import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import VerifiedAddressSearch from "components/address/VerifiedAddressSearch";
import { get, post, put } from "lib/api";
import { useAuth } from "context/AuthContext";
import { getStorefrontConfig } from "lib/storefront";
import { countryForCompany } from "lib/companyProfile";
import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import { isPortalRole } from "lib/portalRoles";
import {
  applyAddressCandidate,
  createAddressRow,
  isAddressRowFilled,
  isVerifiedAddressRow,
  normalizeAddressRow,
  savedAddressLookupLabel,
  stripAddressRowForSave,
  useStorefrontAddressLookup,
} from "lib/storefrontAddressLookup";
import useStorefrontCopy from "hooks/useStorefrontCopy";

const fiscalRegimeOptions = [
  { value: "601", label: "601 - General de Ley Personas Morales" },
  { value: "603", label: "603 - Personas Morales con Fines no Lucrativos" },
  {
    value: "605",
    label: "605 - Sueldos y Salarios e Ingresos Asimilados a Salarios",
  },
  { value: "606", label: "606 - Arrendamiento" },
  {
    value: "612",
    label:
      "612 - Personas Fisicas con Actividades Empresariales y Profesionales",
  },
  { value: "616", label: "616 - Sin obligaciones fiscales" },
  { value: "621", label: "621 - Incorporacion Fiscal" },
  { value: "626", label: "626 - Regimen Simplificado de Confianza" },
];

const cfdiUseOptions = [
  { value: "G01", label: "G01 - Adquisicion de mercancias" },
  { value: "G02", label: "G02 - Devoluciones, descuentos o bonificaciones" },
  { value: "G03", label: "G03 - Gastos en general" },
  { value: "I03", label: "I03 - Equipo de transporte" },
  { value: "I04", label: "I04 - Equipo de computo y accesorios" },
  { value: "I08", label: "I08 - Otra maquinaria y equipo" },
  { value: "S01", label: "S01 - Sin efectos fiscales" },
];

function normalizeList(values, mapper = (value) => String(value || "").trim()) {
  const list = Array.isArray(values) ? values : values == null ? [] : [values];
  const seen = new Set();
  const normalized = [];
  list.forEach((value) => {
    const clean = mapper(value);
    if (!clean || seen.has(clean)) return;
    seen.add(clean);
    normalized.push(clean);
  });
  return normalized;
}

function ensureEditableList(values) {
  return values.length ? values : [""];
}

export default function CustomerProfile() {
  const { t } = useStorefrontCopy();
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const storefront = getStorefrontConfig();
  const isSpanishText = String(storefront?.locale || "")
    .toLowerCase()
    .startsWith("es");
  const isMexicoCompany = countryForCompany(storefront?.companyKey) === "MX";

  const role = String(pluginRole || "").toLowerCase();
  const isPortal = isPortalRole(role);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletionConfirm, setDeletionConfirm] = useState(false);
  const [requestingDeletion, setRequestingDeletion] = useState(false);
  const [message, setMessage] = useState(null);

  const [profile, setProfile] = useState({
    displayName: "",
    emails: [""],
    phones: [""],
    rfc: "",
    taxLegalName: "",
    regimenFiscal: "",
    usoCfdi: "",
    fiscalZip: "",
    facturacionEmail: "",
    fiscalAddress: normalizeAddressRow(
      createAddressRow("mx", { label: "Fiscal" })
    ),
    wantsFacturaMxByDefault: false,
    taxValidationState: "",
    taxValidationMessage: "",
  });
  const fiscalAddressLookup = useStorefrontAddressLookup();

  useEffect(() => {
    fiscalAddressLookup.syncRows(
      [profile.fiscalAddress],
      () => "fiscalAddress",
      savedAddressLookupLabel
    );
  }, [profile.fiscalAddress, fiscalAddressLookup.syncRows]);

  const requiredMissing = useMemo(() => {
    if (!isMexicoCompany || !profile.wantsFacturaMxByDefault) return [];
    const missing = [];
    if (!String(profile.rfc || "").trim()) missing.push("RFC");
    if (!String(profile.regimenFiscal || "").trim())
      missing.push("Regimen Fiscal");
    if (!String(profile.usoCfdi || "").trim()) missing.push("Uso CFDI");
    if (!String(profile.fiscalZip || "").trim()) missing.push("Fiscal ZIP");
    return missing;
  }, [isMexicoCompany, profile]);

  useEffect(() => {
    if (!token || !isPortal) return;
    (async () => {
      setLoading(true);
      setMessage(null);
      try {
        const res = await get("/api/storefront/me");
        const customer = res?.data || null;
        const storefrontProfile = res?.profile || {};
        const emails = normalizeList(
          [
            ...(Array.isArray(storefrontProfile?.emails)
              ? storefrontProfile.emails
              : []),
            ...(Array.isArray(customer?.emails) ? customer.emails : []),
            storefrontProfile?.email,
          ],
          (value) =>
            String(value || "")
              .trim()
              .toLowerCase()
        );
        const phones = normalizeList(
          [
            ...(Array.isArray(storefrontProfile?.phones)
              ? storefrontProfile.phones
              : []),
            ...(Array.isArray(customer?.phones) ? customer.phones : []),
            storefrontProfile?.phone,
          ],
          (value) => String(value || "").trim()
        );

        setProfile({
          displayName: storefrontProfile?.displayName || customer?.name || "",
          emails: ensureEditableList(emails),
          phones: ensureEditableList(phones),
          rfc: customer?.rfc || customer?.taxId || "",
          taxLegalName: customer?.taxLegalName || customer?.name || "",
          regimenFiscal: customer?.regimenFiscal || "",
          usoCfdi: customer?.usoCfdi || "",
          fiscalZip:
            customer?.fiscalZip || customer?.fiscalAddress?.postalCode || "",
          facturacionEmail: customer?.facturacionEmail || "",
          fiscalAddress: normalizeAddressRow(
            customer?.fiscalAddress ||
              createAddressRow("mx", { label: "Fiscal" }),
            "mx"
          ),
          wantsFacturaMxByDefault: !!customer?.wantsFacturaMxByDefault,
          taxValidationState: customer?.taxValidationState || "",
          taxValidationMessage: customer?.taxValidationMessage || "",
        });
      } catch (e) {
        const msg =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to load profile";
        setMessage({ type: "error", text: msg });
      } finally {
        setLoading(false);
      }
    })();
  }, [token, isPortal]);

  if (!token)
    return (
      <Navigate
        to="/authentication/sign-in"
        state={{ from: location }}
        replace
      />
    );
  if (authLoading || pluginRole == null) return null;
  if (!isPortal) return <Navigate to="/dashboard" replace />;

  const updateListValue = (key, index, value) => {
    setProfile((prev) => {
      const copy = [...prev[key]];
      copy[index] = value;
      return { ...prev, [key]: copy };
    });
  };

  const addListValue = (key) => {
    setProfile((prev) => ({ ...prev, [key]: [...prev[key], ""] }));
  };

  const removeListValue = (key, index) => {
    setProfile((prev) => {
      const next = prev[key].filter((_, itemIndex) => itemIndex !== index);
      return { ...prev, [key]: next.length ? next : [""] };
    });
  };

  const onSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const emails = normalizeList(profile.emails, (value) =>
        String(value || "")
          .trim()
          .toLowerCase()
      );
      const phones = normalizeList(profile.phones, (value) =>
        String(value || "").trim()
      );
      const primaryEmail = emails[0] || "";
      const primaryPhone = phones[0] || "";

      if (!primaryEmail || !primaryEmail.includes("@")) {
        throw new Error("Enter at least one valid email.");
      }
      const fiscalAddress = stripAddressRowForSave({
        ...profile.fiscalAddress,
        label: profile.fiscalAddress?.label || "Fiscal",
        postalCode:
          profile.fiscalZip || profile.fiscalAddress?.postalCode || "",
      });
      if (
        isAddressRowFilled(fiscalAddress) &&
        !isVerifiedAddressRow(fiscalAddress)
      ) {
        throw new Error(
          "Select a verified fiscal address from Google suggestions."
        );
      }

      await put("/api/storefront/me", {
        data: {
          displayName: profile.displayName || null,
          email: primaryEmail,
          emails,
          phone: primaryPhone || null,
          phones,
          rfc: profile.rfc || null,
          taxLegalName: profile.taxLegalName || null,
          regimenFiscal: profile.regimenFiscal || null,
          usoCfdi: profile.usoCfdi || null,
          fiscalZip: profile.fiscalZip || null,
          facturacionEmail: profile.facturacionEmail || null,
          fiscalAddress: isAddressRowFilled(fiscalAddress)
            ? fiscalAddress
            : null,
          wantsFacturaMxByDefault: !!profile.wantsFacturaMxByDefault,
        },
      });
      setMessage({ type: "success", text: "Profile saved." });
    } catch (e) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.message ||
        "Failed to save profile";
      setMessage({ type: "error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  const onRequestDeletion = async () => {
    if (!deletionConfirm) {
      setDeletionConfirm(true);
      return;
    }
    setRequestingDeletion(true);
    setMessage(null);
    try {
      await post("/api/storefront/me/delete-request", { data: {} });
      setDeletionConfirm(false);
      setMessage({
        type: "success",
        text: "Account deletion request submitted. We will review it and follow up by email.",
      });
    } catch (e) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.message ||
        "Unable to submit account deletion request.";
      setMessage({ type: "error", text: msg });
    } finally {
      setRequestingDeletion(false);
    }
  };

  return (
    <CustomerPortalLayout
      title={isSpanishText ? "Perfil" : "Profile"}
      subtitle={
        isSpanishText
          ? "Datos de cuenta y factura."
          : "Account and invoice preferences."
      }
      actions={
        <MDButton
          variant="gradient"
          color="info"
          onClick={onSave}
          disabled={saving || loading}
        >
          {saving
            ? isSpanishText
              ? "Guardando..."
              : "Saving..."
            : isSpanishText
            ? "Guardar"
            : "Save"}
        </MDButton>
      }
    >
      {message ? (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack spacing={2}>
            <Box>
              <MDTypography variant="h6" fontWeight={800}>
                Account contact
              </MDTypography>
              <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
                Keep your primary email and phone first so web and iOS stay
                aligned.
              </MDTypography>
            </Box>

            <TextField
              label={isSpanishText ? "Nombre para la cuenta" : "Display name"}
              fullWidth
              value={profile.displayName}
              onChange={(e) =>
                setProfile((p) => ({ ...p, displayName: e.target.value }))
              }
              disabled={loading}
            />

            <Stack spacing={1.25}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <MDTypography variant="button" fontWeight="bold">
                  Emails
                </MDTypography>
                <MDButton
                  variant="text"
                  color="info"
                  onClick={() => addListValue("emails")}
                  disabled={loading || saving}
                >
                  Add email
                </MDButton>
              </Box>
              {profile.emails.map((value, index) => (
                <Box
                  key={`email-${index}`}
                  sx={{ display: "flex", gap: 1, alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    label={index === 0 ? "Primary email" : `Email ${index + 1}`}
                    value={value}
                    onChange={(e) =>
                      updateListValue("emails", index, e.target.value)
                    }
                    disabled={loading || saving}
                  />
                  <IconButton
                    color="error"
                    onClick={() => removeListValue("emails", index)}
                    disabled={loading || saving || profile.emails.length === 1}
                  >
                    <Icon>remove_circle</Icon>
                  </IconButton>
                </Box>
              ))}
            </Stack>

            <Stack spacing={1.25}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <MDTypography variant="button" fontWeight="bold">
                  Phone numbers
                </MDTypography>
                <MDButton
                  variant="text"
                  color="info"
                  onClick={() => addListValue("phones")}
                  disabled={loading || saving}
                >
                  Add phone
                </MDButton>
              </Box>
              {profile.phones.map((value, index) => (
                <Box
                  key={`phone-${index}`}
                  sx={{ display: "flex", gap: 1, alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    label={index === 0 ? "Primary phone" : `Phone ${index + 1}`}
                    value={value}
                    onChange={(e) =>
                      updateListValue("phones", index, e.target.value)
                    }
                    disabled={loading || saving}
                  />
                  <IconButton
                    color="error"
                    onClick={() => removeListValue("phones", index)}
                    disabled={loading || saving || profile.phones.length === 1}
                  >
                    <Icon>remove_circle</Icon>
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack spacing={2}>
            <Box>
              <MDTypography variant="h6" fontWeight={800}>
                {isSpanishText ? "Factura y SAT" : "Invoice preferences"}
              </MDTypography>
              <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
                {isSpanishText
                  ? "Datos fiscales para checkout y facturacion."
                  : "Invoice and tax information for checkout."}
              </MDTypography>
            </Box>

            {isMexicoCompany ? (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!profile.wantsFacturaMxByDefault}
                      onChange={(e) =>
                        setProfile((p) => ({
                          ...p,
                          wantsFacturaMxByDefault: e.target.checked,
                        }))
                      }
                      disabled={loading}
                    />
                  }
                  label={
                    isSpanishText
                      ? "Factura por defecto (a tu RFC)"
                      : "Factura by default (to your RFC)"
                  }
                />
                <MDTypography
                  variant="caption"
                  display="block"
                  color="text"
                  sx={{ opacity: 0.7 }}
                >
                  If off, we still generate an invoice, but it will be issued to
                  “PUBLICO EN GENERAL”.
                </MDTypography>
              </Box>
            ) : null}

            {isMexicoCompany && requiredMissing.length ? (
              <Alert severity="warning">
                Missing required SAT fields for checkout:{" "}
                {requiredMissing.join(", ")}
              </Alert>
            ) : null}

            <TextField
              label="RFC"
              fullWidth
              value={profile.rfc}
              onChange={(e) =>
                setProfile((p) => ({ ...p, rfc: e.target.value }))
              }
              disabled={loading}
            />
            <TextField
              label={isSpanishText ? "Razón social" : "Legal Name"}
              fullWidth
              value={profile.taxLegalName}
              onChange={(e) =>
                setProfile((p) => ({ ...p, taxLegalName: e.target.value }))
              }
              disabled={loading}
            />
            <FormControl fullWidth disabled={loading}>
              <InputLabel id="profile-regimen-fiscal-label">
                {isSpanishText ? "Régimen Fiscal" : "Regimen Fiscal"}
              </InputLabel>
              <Select
                labelId="profile-regimen-fiscal-label"
                label={isSpanishText ? "Régimen Fiscal" : "Regimen Fiscal"}
                value={profile.regimenFiscal}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, regimenFiscal: e.target.value }))
                }
              >
                {fiscalRegimeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={loading}>
              <InputLabel id="profile-uso-cfdi-label">Uso CFDI</InputLabel>
              <Select
                labelId="profile-uso-cfdi-label"
                label="Uso CFDI"
                value={profile.usoCfdi}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, usoCfdi: e.target.value }))
                }
              >
                {cfdiUseOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={isSpanishText ? "Código postal fiscal" : "Fiscal ZIP"}
              fullWidth
              value={profile.fiscalZip}
              onChange={(e) =>
                setProfile((p) => ({ ...p, fiscalZip: e.target.value }))
              }
              disabled={loading}
            />
            <TextField
              label={isSpanishText ? "Correo para factura" : "Invoice Email"}
              fullWidth
              value={profile.facturacionEmail}
              onChange={(e) =>
                setProfile((p) => ({ ...p, facturacionEmail: e.target.value }))
              }
              disabled={loading}
            />
            <VerifiedAddressSearch
              rowKey="fiscalAddress"
              address={profile.fiscalAddress}
              lookup={fiscalAddressLookup}
              disabled={loading}
              label={isSpanishText ? "Domicilio fiscal" : "Fiscal Address"}
              placeholder={
                isSpanishText
                  ? "Busca negocio o domicilio..."
                  : "Search business or address..."
              }
              helperText={
                isSpanishText
                  ? "Selecciona el lugar exacto y confirma el pin en el mapa."
                  : "Select the exact place and confirm the pin on the map."
              }
              onTyping={(nextValue) => {
                setProfile((p) => ({
                  ...p,
                  fiscalAddress: {
                    ...p.fiscalAddress,
                    formattedAddress: nextValue,
                    placeId: "",
                  },
                }));
              }}
              onSelect={(option) => {
                setProfile((p) => {
                  const next = applyAddressCandidate(p.fiscalAddress, option);
                  return {
                    ...p,
                    fiscalAddress: { ...next, label: next.label || "Fiscal" },
                    fiscalZip: next.postalCode || p.fiscalZip,
                  };
                });
              }}
            />
            <TextField
              label={
                isSpanishText ? "Estado validación SAT" : "Tax Validation State"
              }
              fullWidth
              value={profile.taxValidationState || "unknown"}
              disabled
            />
            {profile.taxValidationMessage ? (
              <TextField
                label={
                  isSpanishText ? "Mensaje validación" : "Validation Message"
                }
                fullWidth
                multiline
                minRows={2}
                value={profile.taxValidationMessage}
                disabled
              />
            ) : null}
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, md: 2.5 }, borderColor: "error.light" }}
        >
          <Stack spacing={1.5}>
            <Box>
              <MDTypography variant="h6" fontWeight={800} color="error">
                Delete account
              </MDTypography>
              <MDTypography
                variant="button"
                color="text"
                sx={{ opacity: 0.75 }}
              >
                Request deletion of your customer or distributor account. Some
                order, invoice, warranty, return, support, and security records
                may be retained where required.
              </MDTypography>
            </Box>
            {deletionConfirm ? (
              <Alert severity="warning">
                {t(
                  "profile.deleteRequestText",
                  "This sends a verified deletion request. We will review it and follow up by email."
                )}
              </Alert>
            ) : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {deletionConfirm ? (
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => setDeletionConfirm(false)}
                  disabled={requestingDeletion}
                >
                  Cancel
                </MDButton>
              ) : null}
              <MDButton
                variant="outlined"
                color="error"
                onClick={onRequestDeletion}
                disabled={requestingDeletion}
              >
                {requestingDeletion
                  ? "Submitting..."
                  : deletionConfirm
                  ? "Submit request"
                  : "Delete account"}
              </MDButton>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </CustomerPortalLayout>
  );
}
